<template>
    <div class="card mb-3">
        <div class="section-title-bar">
            <h5 class="section-title">Commission Statement</h5>
        </div>

        <div class="section-body">
          <div class="row g-2 align-items-end mb-3">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label class="filter-label">Select Month</label>
              <vField
                  v-model="commissionMonth"
                  name="allocationMonth"
                  type="month"
                  class="form-control"
              />
            </div>
            <div class="col-12 col-sm-auto">
              <button
                  @click="getCommissionData"
                  class="btn btn-primary waves-effect waves-float w-100"
              >GO</button>
            </div>
          </div>

            <div class="commission-layout" v-if="commissionLoaded">
                <div class="commission-summary-card">
                    <table class="commission-summary-table">
                        <thead>
                            <tr>
                                <th class="text-white">PARTICULARS</th>
                                <th class="text-right text-white">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>A. Personal Sales Commission</td>
                                <td class="text-right">{{ commaFormat(totalCommission.personal_sales_commission) }}</td>
                            </tr>
                            <tr>
                                <td>B. Group Sales Commission</td>
                                <td class="text-right">{{ commaFormat(totalCommission.total_group_commission_amount) }}</td>
                            </tr>
                            <tr>
                                <td>C. Company Sales Share</td>
                                <td class="text-right">{{ commaFormat(totalCommission.total_company_sale_share) }}</td>
                            </tr>
                            <tr>
                                <td>D. BP Commission</td>
                                <td class="text-right">{{ commaFormat(totalCommission.total_group_bp_commission_amount) }}</td>
                            </tr>
                            <tr class="total-row">
                                <td><strong>TOTAL COMMISSION</strong></td>
                                <td class="text-right"><strong>{{ commaFormat(totalCommission.total_commission) }}</strong></td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="download-btn-wrap">
                        <button @click="downloadStatement" class="btn-download">
                            <i class="fa fa-download"></i> Download - Statement
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="!commissionLoaded && !commissionLoading" class="empty-commission">
                <i class="fa fa-bar-chart"></i>
                <p>Select month and staff ID, then click GO to view commission data</p>
            </div>
        </div>
        <Loader v-if="commissionLoading" />
    </div>
</template>

<script setup>
import {inject, ref, watch} from 'vue';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import handleSdm from '@/services/modules/sdm/sdm';
import figureFormatter from '@/services/utils/figureFormatter';
import saleCommissionPdf from '@/services/utils/pdf/saleCommissionDataPdf';

const props = defineProps({
    staffId: {
      type: [Number, String],
      default: null
    },
    defaultMonth: {
      type: String,
      default: ''
    },
    companyInfo: {
      type: Object,
      default: () => ({})
    },
});

const route = useRoute();
const showError = inject('showError');
const { commaFormat } = figureFormatter();
const { exportToPDF } = saleCommissionPdf();
const { fetchSaleCommissionData } = handleSdm();

const commissionMonth = ref(props.defaultMonth);
const commissionStaffId = ref(props.staffId);
const commissionLoading = ref(false);
const commissionLoaded = ref(false);
const totalCommission = ref({});

watch(() => props.staffId, (val) => {
  commissionStaffId.value = val;
});

const getCommissionQuery = () => {
    let q = `?company_id=${route.params.companyId}`;
    if (commissionStaffId.value) q += `&staff_id=${commissionStaffId.value}`;
    if (commissionMonth.value) q += `&month=${commissionMonth.value}`;
    return q;
};

const getCommissionData = async () => {
    commissionLoading.value = true;
    commissionLoaded.value = false;
    totalCommission.value = {};
    await fetchSaleCommissionData(getCommissionQuery()).then(res => {
        if (!res.status) {
          return;
        }
        totalCommission.value = res.data.total;
        commissionLoaded.value = true;
    }).catch(() => {}).finally(() => {
      commissionLoading.value = false;
    });
};

const downloadStatement = async () => {
    const query = getCommissionQuery() + '&is_all=1';
    await fetchSaleCommissionData(query).then(res => {
        if (!res.status) {
          showError(res.message);
          return;
        }
        const data = res.data;
        exportToPDF(props.companyInfo, data.personal_sale_commission, data.group_sale_commission, data.company_sale_share, data.total, commissionMonth.value);
    }).catch(() => {});
};
</script>

<style scoped>
.section-title-bar {
  background: linear-gradient(90deg, #1e3a5f 0%, #2d6a9f 100%);
  padding: 0.75rem 1.25rem;
}

.section-title {
    margin: 0;
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.03em;
}

.section-body {
    padding: 1.25rem;
}

.filter-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.commission-layout {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.commission-summary-card {
    flex: 0 0 auto;
    width: 100%;
    max-width: 420px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(99,102,241,0.1);
    border: 1px solid #e0e7ff;
}

.commission-summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.commission-summary-table thead tr {
    background: linear-gradient(90deg, #6366f1, #818cf8);
    color: #fff;
}

.commission-summary-table th {
    padding: 0.75rem 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.commission-summary-table td {
    padding: 0.65rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
}

.commission-summary-table .total-row {
    background: #ecfdf5;
}

.commission-summary-table .total-row td {
    color: #059669;
    font-size: 0.95rem;
    border-bottom: none;
}

.download-btn-wrap {
    display: flex;
    justify-content: center;
    padding: 0.75rem;
    background: #f8f9ff;
    border-top: 1px solid #e0e7ff;
}

.btn-download {
    background: linear-gradient(135deg, #06b6d4, #0891b2);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    justify-content: center;
    letter-spacing: 0.03em;
    transition: opacity 0.15s;
}

.btn-download:hover { opacity: 0.9; }

.empty-commission {
    text-align: center;
    padding: 3rem 1rem;
    color: #9ca3af;
}

.empty-commission i {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    display: block;
}

.empty-commission p {
    font-size: 0.875rem;
    margin: 0;
}

.text-right { text-align: right; }
</style>
